import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ImageIcon, Plus, Menu, X, HelpCircle, Heart, MessageSquare, FileText, Wallet, Unlock, Lock, ScrollText } from 'lucide-react';
import { useAccount } from 'wagmi';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import HowItWorksModal from './HowItWorksModal';
import SupportedCharitiesModal from './SupportedCharitiesModal';
import FAQModal from './FAQModal';
import ContactModal from './ContactModal';
import WalletModal from './WalletModal';
import TermsModal from './TermsModal';
import './MiniAppBottomNav.css';

const MiniAppBottomNav = ({ confessionGalleryRef, unlockedCount = 0 }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMiniApp, setIsMiniApp] = useState(false);
  const { address, isConnected } = useAccount();
  
  // Modal states
  const [showHowItWorksModal, setShowHowItWorksModal] = useState(false);
  const [showCharitiesModal, setShowCharitiesModal] = useState(false);
  const [showFAQModal, setShowFAQModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showWalletModal, setShowWalletModal] = useState(false);

  useEffect(() => {
    // Detect mobile device with multiple methods (same logic as MiniAppWrapper)
    const isMobileUserAgent = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const isMobileWidth = window.innerWidth <= 768;
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const isTabletWidth = window.innerWidth <= 1024;
    const isMobileDevice = isMobileUserAgent || isMobileWidth || (isTouchDevice && isTabletWidth);
    
    // Check if we're in miniapp mode OR mobile device
    const isMiniAppMode = window?.parent !== window || 
                         navigator.userAgent.includes('MiniApp') ||
                         window.location.search.includes('miniapp=true') ||
                         isMobileDevice;
    setIsMiniApp(isMiniAppMode);
  }, []);

  const menuItems = [
    // Wallet section
    ...(isConnected ? [
      {
        icon: Unlock,
        label: `Unlocked Confessions (${unlockedCount})`,
        onClick: () => {
          document.getElementById('confessions')?.scrollIntoView({ behavior: 'smooth' });
          setTimeout(() => {
            confessionGalleryRef?.current?.switchToUnlockedTab();
          }, 500);
          setIsMenuOpen(false);
        }
      },
      {
        icon: Wallet,
        label: 'Wallet',
        onClick: () => {
          setShowWalletModal(true);
          setIsMenuOpen(false);
        }
      }
    ] : []),
    
    // Main menu items
    {
      icon: HelpCircle,
      label: 'How it works',
      onClick: () => {
        setShowHowItWorksModal(true);
        setIsMenuOpen(false);
      }
    },
    {
      icon: MessageSquare,
      label: 'FAQ',
      onClick: () => {
        setShowFAQModal(true);
        setIsMenuOpen(false);
      }
    },
    {
      icon: Heart,
      label: 'Supported Charities',
      onClick: () => {
        setShowCharitiesModal(true);
        setIsMenuOpen(false);
      }
    },
    {
      icon: ScrollText,
      label: 'Terms',
      onClick: () => {
        setShowTermsModal(true);
        setIsMenuOpen(false);
      }
    },
    {
      icon: FileText,
      label: 'Contact us',
      onClick: () => {
        setShowContactModal(true);
        setIsMenuOpen(false);
      }
    }
  ];

  const handleGalleryClick = () => {
    document.getElementById('confessions')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubmitClick = () => {
    document.getElementById('submit')?.scrollIntoView({ behavior: 'smooth' });
  };

  // Don't render if not in miniapp mode
  if (!isMiniApp) {
    return null;
  }

  return (
    <>
      {/* Bottom Navigation Bar */}
      <motion.div
        className="miniapp-bottom-nav"
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.3 }}
      >
        <motion.button
          className="nav-item"
          onClick={handleGalleryClick}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <ImageIcon size={24} />
          <span>Gallery</span>
        </motion.button>

        <motion.button
          className="nav-item"
          onClick={handleSubmitClick}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <Plus size={28} />
          <span>Confess</span>
        </motion.button>

        <motion.button
          className="nav-item"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          <span>Menu</span>
        </motion.button>
      </motion.div>

      {/* Menu Overlay */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            className="miniapp-menu-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsMenuOpen(false)}
          >
            <motion.div
              className="miniapp-menu-bottom"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 50 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="miniapp-menu-header">
                <h3>Menu</h3>
                <button
                  className="miniapp-menu-close"
                  onClick={() => setIsMenuOpen(false)}
                  aria-label="Close menu"
                >
                  <X size={20} />
                </button>
              </div>
              
              <div className="miniapp-menu-items">
                {/* Connect Wallet Button (only if not connected) */}
                {!isConnected && (
                  <div className="wallet-connect-section">
                    <ConnectButton.Custom>
                      {({
                        account,
                        chain,
                        openConnectModal,
                        mounted,
                      }) => {
                        const ready = mounted;
                        const connected = ready && account && chain;

                        return (
                          <div
                            {...(!ready && {
                              'aria-hidden': true,
                              'style': {
                                opacity: 0,
                                pointerEvents: 'none',
                                userSelect: 'none',
                              },
                            })}
                          >
                            {!connected && (
                              <motion.button
                                onClick={() => {
                                  openConnectModal();
                                  setIsMenuOpen(false);
                                }}
                                className="miniapp-menu-item wallet-connect-btn"
                                whileHover={{ backgroundColor: 'rgba(102, 126, 234, 0.2)' }}
                                whileTap={{ scale: 0.98 }}
                              >
                                <Wallet size={20} />
                                <span>Connect Wallet</span>
                              </motion.button>
                            )}
                          </div>
                        );
                      }}
                    </ConnectButton.Custom>
                    
                    <div className="no-wallet-info">
                      <Lock size={16} />
                      <span>Connect wallet to unlock confessions</span>
                    </div>
                  </div>
                )}

                {/* Menu Items */}
                {menuItems.map((item, index) => (
                  <motion.button
                    key={index}
                    className="miniapp-menu-item"
                    onClick={item.onClick}
                    whileHover={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <item.icon size={20} />
                    <span>{item.label}</span>
                  </motion.button>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modals */}
      <HowItWorksModal
        isOpen={showHowItWorksModal}
        onClose={() => setShowHowItWorksModal(false)}
      />
      
      <SupportedCharitiesModal
        isOpen={showCharitiesModal}
        onClose={() => setShowCharitiesModal(false)}
      />
      
      <FAQModal
        isOpen={showFAQModal}
        onClose={() => setShowFAQModal(false)}
      />
      
      <TermsModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
      />
      
      <ContactModal
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
      />
      
      <WalletModal 
        isOpen={showWalletModal} 
        onClose={() => setShowWalletModal(false)}
        unlockedCount={unlockedCount}
      />
    </>
  );
};

export default MiniAppBottomNav;